import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, AlertTriangle, Globe, FileText, Shield, Ban } from 'lucide-react';
import axios from 'axios';

const TradePolicyTracker = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMineral, setSelectedMineral] = useState('');
  const [availableMinerals, setAvailableMinerals] = useState([]);

  // Color schemes for charts
  const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#f97316'];

  const fetchMinerals = async () => {
    try {
      const response = await axios.get('/api/trade-policy-tracker/minerals');
      if (response.data.success) {
        setAvailableMinerals(response.data.minerals);
      }
    } catch (err) {
      console.error('Error fetching minerals:', err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedMineral ? { mineral: selectedMineral } : {};
      const response = await axios.get('/api/trade-policy-tracker/kpis', { params });
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('Failed to fetch trade policy data');
      }
    } catch (err) {
      console.error('Error fetching trade policy data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedMineral]);

  useEffect(() => {
    fetchMinerals();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedMineral, fetchData]);

  const handleMineralChange = (event) => {
    setSelectedMineral(event.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trade policy data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { kpis, export_bans_by_country, mineral_ranking, restriction_types } = data;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Compact Widget Header */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Shield className="h-6 w-6 mr-2 text-primary-600" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Trade Policy Tracker
              </h1>
            </div>
            <div>
              <select
                value={selectedMineral}
                onChange={handleMineralChange}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Minerals</option>
                {availableMinerals.map(mineral => (
                  <option key={mineral} value={mineral}>
                    {mineral}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Compact IEA KPI Metrics */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <Globe className="h-5 w-5 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {kpis.countries_with_policies}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Countries with {selectedMineral ? `${selectedMineral} ` : ''}Policies
              </div>
            </div>
            
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
              <Ban className="h-5 w-5 text-red-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {kpis.countries_with_export_bans}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Export Restrictions
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                {kpis.total_export_restrictions} total
              </div>
            </div>
            
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <TrendingUp className="h-5 w-5 text-orange-500 mx-auto mb-2" />
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {selectedMineral || mineral_ranking[0]?.mineral || 'N/A'}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {selectedMineral ? 'Selected Mineral' : 'Most Affected'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-500">
                {mineral_ranking[0]?.total_restrictions || 0} restrictions
              </div>
            </div>
          </div>
        </div>

        {/* Compact Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Export Restrictions Chart */}
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-primary-600" />
              {selectedMineral ? `${selectedMineral} ` : ''}Export Restrictions by Country
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={export_bans_by_country} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="country" 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                  fontSize={11}
                />
                <YAxis fontSize={11} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#ef4444"
                  name="Restrictions"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Quick Stats Panel */}
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 text-primary-600" />
              Policy Overview
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total Countries</span>
                <span className="text-lg font-bold text-blue-600">{kpis.countries_with_policies}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">With Restrictions</span>
                <span className="text-lg font-bold text-red-600">{kpis.countries_with_export_bans}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Total Restrictions</span>
                <span className="text-lg font-bold text-orange-600">{kpis.total_export_restrictions}</span>
              </div>
              {/* <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Restriction Rate</span>
                <span className="text-lg font-bold text-purple-600">
                  {((kpis.countries_with_export_bans / kpis.countries_with_policies) * 100).toFixed(1)}%
                </span>
              </div> */}
              
              {/* Restriction Types */}
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Restriction Types:</h4>
                <div className="space-y-1">
                  {restriction_types.map((type, index) => (
                    <div key={type.type} className="flex justify-between items-center text-xs">
                      <span className="text-gray-600 dark:text-gray-400 capitalize">{type.type}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{type.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Footer */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-full">
            <FileText className="h-4 w-4 text-blue-500 mr-2" />
            <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">
              IEA Trade Policy KPIs • Critical Minerals Tracker
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradePolicyTracker;
